import { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Receipt, Save, CheckCircle } from "lucide-react";

const TIERS = [
  { value: "clarolight_pro", label: "ClaroShield Light" },
  { value: "shield_full", label: "ClaroShield (Enterprise)" },
];
const METHODS = [
  { value: "wire", label: "Wire transfer" },
  { value: "ach", label: "ACH" },
  { value: "credit_card", label: "One-time credit card" },
];
const fmt = (iso) => { try { return new Date(iso).toLocaleString(); } catch { return iso; } };

function PaymentInstructions() {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/console/payment-instructions").then(({ data }) => setText(data.text || "")).catch(() => {}); }, []);
  const save = async () => {
    setBusy(true);
    try { await api.put("/console/payment-instructions", { text }); toast.success("Payment instructions saved"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not save"); }
    finally { setBusy(false); }
  };
  return (
    <div className="border border-slate-200 bg-white p-5 space-y-3" data-testid="payment-instructions-editor">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
        <Receipt size={18} className="text-primary" /> Customer payment instructions
      </div>
      <p className="text-sm text-slate-600">Shown to customers on their Plan & Billing page. Include your wire/ACH details.</p>
      <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={8} className="rounded-none font-mono text-xs"
        data-testid="payment-instructions-text" />
      <Button onClick={save} disabled={busy} className="rounded-none" data-testid="payment-instructions-save">
        <Save size={15} className="mr-1" /> {busy ? "Saving…" : "Save"}
      </Button>
    </div>
  );
}

function ProvisionForm({ tenants, onDone }) {
  const [tid, setTid] = useState("");
  const [tier, setTier] = useState("clarolight_pro");
  const [amount, setAmount] = useState("");
  const [invoice, setInvoice] = useState("");
  const [po, setPo] = useState("");
  const [method, setMethod] = useState("wire");
  const [noExpiry, setNoExpiry] = useState(false);
  const [termEnd, setTermEnd] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!tid) { toast.error("Select an entity"); return; }
    setBusy(true);
    try {
      await api.post(`/console/tenant/${tid}/provision`, {
        tier, amount: amount ? Number(amount) : null, invoice_number: invoice || null,
        po_number: po || null, payment_method: method,
        term_end: noExpiry ? null : (termEnd || null), notes: notes || null,
      });
      toast.success("Entity activated");
      setAmount(""); setInvoice(""); setPo(""); setNotes(""); setTermEnd("");
      onDone && onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not provision"); }
    finally { setBusy(false); }
  };

  return (
    <div className="border border-slate-200 bg-white p-5 space-y-4" data-testid="provision-form">
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
        <CheckCircle size={18} className="text-primary" /> Activate / upgrade an entity (by invoice)
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="space-y-1">
          <label className="text-xs text-slate-500">Entity</label>
          <Select value={tid} onValueChange={setTid}>
            <SelectTrigger className="rounded-none" data-testid="provision-entity"><SelectValue placeholder="Select entity" /></SelectTrigger>
            <SelectContent>{tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-slate-500">Tier</label>
          <Select value={tier} onValueChange={setTier}>
            <SelectTrigger className="rounded-none" data-testid="provision-tier"><SelectValue /></SelectTrigger>
            <SelectContent>{TIERS.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-slate-500">Amount ($)</label>
          <Input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} className="rounded-none" data-testid="provision-amount" />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-slate-500">Payment method</label>
          <Select value={method} onValueChange={setMethod}>
            <SelectTrigger className="rounded-none" data-testid="provision-method"><SelectValue /></SelectTrigger>
            <SelectContent>{METHODS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-slate-500">Invoice #</label>
          <Input value={invoice} onChange={(e) => setInvoice(e.target.value)} className="rounded-none" data-testid="provision-invoice" />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-slate-500">PO #</label>
          <Input value={po} onChange={(e) => setPo(e.target.value)} className="rounded-none" data-testid="provision-po" />
        </div>
        <div className="space-y-1">
          <label className="text-xs text-slate-500">Term end date</label>
          <Input type="date" value={termEnd} disabled={noExpiry} onChange={(e) => setTermEnd(e.target.value)} className="rounded-none" data-testid="provision-term" />
        </div>
        <div className="flex items-center gap-2 pt-6">
          <Switch checked={noExpiry} onCheckedChange={setNoExpiry} data-testid="provision-no-expiry" />
          <span className="text-sm text-slate-600">No expiry</span>
        </div>
      </div>
      <div className="space-y-1">
        <label className="text-xs text-slate-500">Notes</label>
        <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="rounded-none" data-testid="provision-notes" />
      </div>
      <Button onClick={submit} disabled={busy} className="rounded-none" data-testid="provision-submit">
        {busy ? "Activating…" : "Activate entity"}
      </Button>
    </div>
  );
}

function BillingRecords({ rows }) {
  return (
    <div className="border border-slate-200 bg-white p-5 space-y-3" data-testid="billing-records">
      <div className="text-sm font-semibold text-slate-900">Provisioning history ({rows.length})</div>
      {rows.length === 0 ? <p className="text-sm text-slate-500">No records yet.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase tracking-wide text-slate-500 border-b">
              <th className="py-2 pr-4">Date</th><th className="py-2 pr-4">Entity</th><th className="py-2 pr-4">Tier</th>
              <th className="py-2 pr-4">Amount</th><th className="py-2 pr-4">Invoice</th><th className="py-2 pr-4">PO</th>
              <th className="py-2 pr-4">Method</th><th className="py-2 pr-4">By</th>
            </tr></thead>
            <tbody>{rows.map((r) => (
              <tr key={r.id} className="border-b last:border-0" data-testid={`billing-record-${r.id}`}>
                <td className="py-2 pr-4 text-slate-600">{fmt(r.created_at)}</td>
                <td className="py-2 pr-4 font-medium text-slate-900">{r.registration_name}</td>
                <td className="py-2 pr-4">{r.tier_label}</td>
                <td className="py-2 pr-4">{r.amount != null ? `$${r.amount}` : "—"}</td>
                <td className="py-2 pr-4">{r.invoice_number || "—"}</td>
                <td className="py-2 pr-4">{r.po_number || "—"}</td>
                <td className="py-2 pr-4">{r.payment_method || "—"}</td>
                <td className="py-2 pr-4 text-slate-500">{r.created_by}</td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function ConsoleBilling() {
  const [tenants, setTenants] = useState([]);
  const [records, setRecords] = useState([]);
  const load = useCallback(async () => {
    try {
      const [t, r] = await Promise.all([api.get("/console/tenants"), api.get("/console/billing-records")]);
      setTenants(t.data.tenants || []);
      setRecords(r.data.records || []);
    } catch (e) { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-6" data-testid="console-billing">
      <PaymentInstructions />
      <ProvisionForm tenants={tenants} onDone={load} />
      <BillingRecords rows={records} />
    </div>
  );
}
